import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import Button from '@narvar/nth-button';
import { Container, Row, Column } from '@narvar/nth-grid';
import TextLink from '@narvar/nth-text-link';
import { BigHeader, MediumHeader, SmallHeader } from '@narvar/nth-header';
import Checkbox from '@narvar/nth-checkbox';
import Divider from '@narvar/nth-divider';
import NarvarFooter from '../NarvarFooter';
import Icon from '../Icon';
import PickupDate from './PickupDate';
import PickupLocation from '../PickupLocation';
import InstructionsInput from './InstructionsInput';
import Loader from '../Loader';
import { PICKUPS } from '../../constants/stripeWidgets';
import StripeWidget from '../StripeWidget';
import {
  fetchPickupAddon,
  receivePickupPhoneError,
  receivePickupPhoneNumber,
  receivePickupSubmitting,
  submitPickup,
  receivePickupTimeWindow,
} from '../../actions/pickupAddon';
import PickupTypes from './PickupTypes';
import PickupDatePicker from './PickupDatePicker';
import PhoneNumberForm from '../PhoneNumberForm';
import PickupTimeWindows from './PickupTimeWindows';

const StyledErrors = styled.p`
  margin: 1rem 0;
  color: #eb1c26;
  text-align: center;
`;

export const mapStateToProps = ({
  language,
  retailerSettings: { branding: { brand_call_to_action: ctaColor } = {} } = {},
  pickupAddon: { isFetching, submitting, address, form, error, options: { timeWindows } = {} },
  returnsSettings: {
    pickups: {
      features: { scheduleAhead: { enable: enableScheduleAhead = false } = {} } = {},
    } = {},
  } = {},
}) => ({
  isFetching,
  submitting,
  language,
  address,
  form,
  error,
  ctaColor,
  enableScheduleAhead,
  timeWindows,
});

const PickupScheduling = ({
  dispatch,
  isFetching,
  submitting,
  language,
  address,
  form,
  error,
  ctaColor,
  enableScheduleAhead,
  timeWindows,
}) => {
  const pickupPolicyEnabled = !window.pickupsPolicyCheckboxEnabled === false;
  const [policyChecked, setPolicyChecked] = useState(!pickupPolicyEnabled);

  const timeWindowsEnabled = Boolean(timeWindows && timeWindows.length);

  useEffect(() => {
    dispatch(fetchPickupAddon());
  }, [dispatch]);

  if (isFetching) {
    return (
      <Container style={{ marginTop: '12rem' }}>
        <Row>
          <Column md-columns={8} md-column-offset={2}>
            <Loader />
            <Row style={{ marginTop: '18rem' }}>
              <NarvarFooter />
            </Row>
          </Column>
        </Row>
      </Container>
    );
  }

  const { date, paymentReq, phoneNumber, phoneNumberError, pickupTime } = form;
  const canSubmit = policyChecked && !phoneNumberError && (!timeWindowsEnabled || pickupTime);

  if ((!date || isEmpty(address)) && error) {
    return (
      <Container style={{ textAlign: 'center', marginTop: '12rem' }}>
        <Row>
          <Column md-columns={8} md-column-offset={2}>
            <MediumHeader s={{ margin: '0.75rem auto', fontSize: '1.25rem' }}>
              {language['returns.pickups_page.error.not_eligible.title']}
            </MediumHeader>
            <p>{language['returns.pickups_page.error.not_eligible.description']}</p>
            <Row style={{ marginTop: '18rem' }}>
              <NarvarFooter />
            </Row>
          </Column>
        </Row>
      </Container>
    );
  }

  const PickupPolicy = pickupPolicyEnabled ? (
    <React.Fragment>
      <Row style={{ margin: '1rem 0 2rem 0' }}>
        <Divider s={{ margin: '0', border: '2px solid #e0e0e0' }} />
      </Row>
      <MediumHeader s={{ fontSize: '1rem' }}>
        {language[`returns.pickups_page.pickup_policy.title`]}
      </MediumHeader>
      <p
        style={{
          marginBottom: '1rem',
          color: '#7a7a7a',
          fontFamily: 'inherit',
        }}
      >
        {language[`returns.pickups_page.pickup_policy.description`]}
      </p>

      <Checkbox
        name="pickup_policy_form"
        checked={policyChecked}
        onChange={() => {
          setPolicyChecked(!policyChecked);
        }}
        id="pickup_policy_form"
        boxStyle={{
          width: '1.5rem',
          height: '1.5rem',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: policyChecked ? ctaColor : '#cccccc',
          background: ctaColor,
        }}
      >
        {language['returns.pickups_page.pickup_policy.checkbox_label']}
      </Checkbox>
    </React.Fragment>
  ) : null;

  return (
    <Container style={{ textAlign: 'center' }}>
      <Row style={{ marginTop: '4rem' }}>
        <Column xl-columns={8} xl-column-offset={2} lg-columns={10} lg-column-offset={1}>
          <Row>
            <BigHeader s={{ margin: '0' }}>{language['returns.pickups_page.title']}</BigHeader>
          </Row>
          <Row style={{ margin: '1rem' }}>
            <Icon.Truck s={{ width: '6rem' }} outlineColor={ctaColor} noFill />
          </Row>
          <Row>
            <SmallHeader s={{ margin: '0 1rem' }}>
              {language['returns.pickups_page.description']}
            </SmallHeader>
          </Row>
          <Row style={{ margin: '1rem' }} className="faq-link">
            <TextLink
              s={{
                color: ctaColor,
                hoverColor: ctaColor,
                textDecoration: 'underline',
              }}
              href={language['returns.pickups_page.help_url']}
              target="_blank"
            >
              {language['returns.pickups_page.help_url_text']}
            </TextLink>
          </Row>

          <Row style={{ margin: '2rem auto' }}>
            <Divider s={{ margin: '0', border: '2px solid #e0e0e0' }} />
          </Row>

          <Row style={{ padding: '0 0.5rem' }}>
            <MediumHeader s={{ fontSize: '1rem', margin: '0' }}>
              {language['returns.pickups_page.time.title']}
            </MediumHeader>
            {enableScheduleAhead ? (
              <React.Fragment>
                <p style={{ color: '#7A7A7A', margin: '1.5rem auto', fontFamily: 'inherit' }}>
                  {language['returns.pickups_page.time.subtitle']}
                </p>
                <PickupDatePicker />
              </React.Fragment>
            ) : (
              <PickupDate date={date} />
            )}
            <PickupTimeWindows
              timeWindows={timeWindows}
              allDayString={language['returns.pickups_page.time.footer']}
              onClick={(time) => dispatch(receivePickupTimeWindow(time))}
              selectedPickupTime={pickupTime}
            />
          </Row>

          <Row style={{ margin: '2rem auto' }}>
            <Divider s={{ margin: '0', border: '2px solid #e0e0e0' }} />
          </Row>

          <Row style={{ padding: '0 0.5rem' }}>
            <PickupLocation
              title={language['returns.pickups_page.address.title']}
              subtitle={language['returns.pickups_page.address.subtitle']}
              address={{
                street_1: address.street1,
                street_2: address.street2,
                city: address.city,
                state: address.state,
                zip: address.zip_code,
              }}
            />
            <Column md-columns={8} md-column-offset={2}>
              <PickupTypes />
              <InstructionsInput />
            </Column>
          </Row>

          <Row style={{ margin: '1rem auto' }}>
            <Column md-columns={8} md-column-offset={2}>
              <PhoneNumberForm
                value={phoneNumber}
                onChange={(value) => dispatch(receivePickupPhoneNumber(value))}
                onBlur={() =>
                  dispatch(
                    receivePickupPhoneError(Boolean(phoneNumber && phoneNumber.length !== 10)),
                  )
                }
                header={language['returns.pickups_page.phone_number_form.header']}
                headerTag={language['returns.pickups_page.phone_number_form.header_tag']}
                placeholder={language['returns.pickups_page.phone_number_form.placeholder']}
                footer={language['returns.pickups_page.phone_number_form.footer']}
                error={phoneNumberError}
                errorMessage={language['returns.pickups_page.phone_number_form.error']}
              />
            </Column>
          </Row>

          {paymentReq ? (
            // Payment required - show credit card from plus separator line
            <React.Fragment>
              <Row style={{ margin: '2rem auto' }}>
                <Divider s={{ margin: '0', border: '2px solid #e0e0e0' }} />
              </Row>

              <Row>
                <Column>
                  <StripeWidget
                    id={PICKUPS}
                    onClick={() => dispatch(receivePickupSubmitting(true))}
                    onSuccess={() => dispatch(submitPickup())}
                    onError={() => dispatch(receivePickupSubmitting(false))}
                    confirmingCard={submitting}
                    aboveSubmitContent={PickupPolicy}
                    disabled={!canSubmit}
                  />
                  {error && (
                    <StyledErrors>
                      {language['returns.pickups_page.error.submit_failure.description']}
                    </StyledErrors>
                  )}
                </Column>
              </Row>
            </React.Fragment>
          ) : (
            // No payment required - only show submit button - and pickup details if enabled
            <React.Fragment>
              {PickupPolicy}
              <Row style={{ margin: '2rem auto' }}>
                <Column md-columns={8} md-column-offset={2}>
                  <Button
                    disabled={!canSubmit}
                    className="narvar-btn-primary"
                    big
                    onClick={() => dispatch(submitPickup())}
                    s={{
                      height: '2.5rem',
                      backgroundColor: ctaColor,
                    }}
                  >
                    {language[`returns.pickups_page.submit_button`]}
                  </Button>
                </Column>
              </Row>
            </React.Fragment>
          )}

          <Row style={{ marginTop: '6rem' }}>
            <NarvarFooter />
          </Row>
        </Column>
      </Row>
    </Container>
  );
};

PickupScheduling.defaultProps = {
  ctaColor: '#000000',
  enableScheduleAhead: false,
  enablePhoneNumber: false,
};

PickupScheduling.propTypes = {
  dispatch: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  language: PropTypes.object.isRequired,
  form: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string,
    type: PropTypes.string,
    instructions: PropTypes.string,
    phoneNumber: PropTypes.string,
    phoneNumberError: PropTypes.bool,
  }).isRequired,
  address: PropTypes.shape({
    street1: PropTypes.string,
    street2: PropTypes.string,
    city: PropTypes.string,
    province: PropTypes.string,
    province_code: PropTypes.string,
    zip_code: PropTypes.string,
    country: PropTypes.string,
    country_code: PropTypes.string,
  }).isRequired,
  error: PropTypes.bool.isRequired,
  ctaColor: PropTypes.string,
  enableScheduleAhead: PropTypes.bool,
  enablePhoneNumber: PropTypes.bool,
  timeWindows: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default connect(mapStateToProps)(PickupScheduling);
